"use client"

import { createContext, useContext } from "react"
import type { ActionTimelineStatus } from "../../../../components/ActionTimeline"
import type { TransactionIdentifier } from "../../../../stores/currentTransactionStore"
import type { SwapReceipt } from "../hooks/useSwapReceipt"
import type { TransactionMode } from "../types"
import type { SwapActions, SwapQuote } from "./SwapProvider"

type SwapContextType = {
  isLoadingQuote: boolean
  container: HTMLElement | null
  actions: SwapActions["actions"]
  quote: SwapQuote | undefined
  receipt: SwapReceipt | undefined
  variant: "default" | "frosted"
  isLoadingInitialToCurrency: boolean
  isLoadingInitialFromCurrency: boolean
  hasQuoteError: boolean
  isCurrencySelectionDisabled: boolean
  isAutoSlippageEnabled: boolean
  shouldShowConfirmationModal: boolean
  actionsStatus: ActionTimelineStatus
  source: string | undefined
  transactionMode: TransactionMode
  setTransactionMode: (mode: TransactionMode) => void
  setActionsStatus: (status: ActionTimelineStatus) => void
  toggleAutoSlippage: () => void
  setShouldShowConfirmationModal: (show: boolean) => void
  waitForCompletion: (
    transactionIdentifier: TransactionIdentifier,
  ) => Promise<SwapReceipt>
  setContainer: (container: HTMLElement | null) => void
  onSubmit: () => Promise<void>
  reset: (newMode?: "buy" | "sell") => void
}

export const SwapContext = createContext<SwapContextType>({
  isLoadingQuote: false,
  container: null,
  actions: [],
  quote: undefined,
  receipt: undefined,
  variant: "default",
  isLoadingInitialToCurrency: false,
  isLoadingInitialFromCurrency: false,
  hasQuoteError: false,
  isCurrencySelectionDisabled: false,
  isAutoSlippageEnabled: true,
  shouldShowConfirmationModal: false,
  actionsStatus: { isPending: false, isLoading: false, isDone: false },
  source: undefined,
  transactionMode: "sell",
  setTransactionMode: () => {},
  setActionsStatus: () => {},
  toggleAutoSlippage: () => {},
  setShouldShowConfirmationModal: () => {},
  waitForCompletion: () => Promise.resolve({} as SwapReceipt),
  setContainer: () => {},
  onSubmit: async () => {},
  reset: () => {},
})

export const useSwapContext = () => {
  const context = useContext(SwapContext)
  return context
}
